import React, { lazy, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BoardSettings } from '../../components/board/BoardSettings'
import { ColumnList } from '../../components/board/ColumnList'
import { DeadlineCalendar } from '../../components/board/DeadlineCalendar'
import { Button, LoadingSpinner } from '../../components/common'
import { MainLayout } from '../../components/layout'
import { useDocumentTitle } from '../../hooks'
import { useBoard } from '../../hooks/useBoards'
import { useColumns } from '../../hooks/useColumns'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import './BoardView.css'

// Lazy load CardModal
const CardModal = lazy(() =>
  import('../../components/card/CardModal').then((module) => ({
    default: module.CardModal,
  }))
)

/**
 * BoardView Component
 * Main board display with columns layout
 * Implements Requirements 4.2, 6.1
 */
export const BoardView: React.FC = () => {
  const { t } = useTranslation()
  const { boardId, cardId } = useParams<{ boardId: string; cardId?: string }>()
  const navigate = useNavigate()
  const numericBoardId = Number(boardId)
  const numericCardId = cardId ? Number(cardId) : null
  const [searchOpen, setSearchOpen] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [filterByDate, setFilterByDate] = useState<string | null>(null)

  const {
    data: board,
    isLoading: boardLoading,
    error: boardError,
  } = useBoard(numericBoardId)
  const { data: columns, isLoading: columnsLoading } =
    useColumns(numericBoardId)

  // Set document title with board name
  useDocumentTitle('pageTitles.board', { boardName: board?.name || '' })

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSearch: () => setSearchOpen(true),
    onEscape: () => setSearchOpen(false),
  })

  if (boardLoading || columnsLoading) {
    return (
      <MainLayout title={t('common.loading')}>
        <div className="board-view__loading">
          <LoadingSpinner size="lg" />
        </div>
      </MainLayout>
    )
  }

  if (boardError || !board) {
    return (
      <MainLayout title={t('errors.notFound')}>
        <div className="board-view__error">
          <h2>{t('errors.notFound')}</h2>
          <p>{t('board.boardNotFoundDescription')}</p>
        </div>
      </MainLayout>
    )
  }

  const handleCloseCard = () => {
    navigate(`/boards/${boardId}`)
  }

  const boardActions = (
    <Button onClick={() => setShowSettings(true)} size="md">
      ⚙️ {t('common.settings')}
    </Button>
  )

  return (
    <MainLayout title={board.name} actions={boardActions}>
      <main className="board-view" role="main" aria-label="Board view">
        <DeadlineCalendar
          boardId={numericBoardId}
          onDateFilter={setFilterByDate}
          selectedDate={filterByDate}
        />

        <div
          className="board-view__content"
          role="region"
          aria-label="Board columns"
        >
          <ColumnList
            boardId={numericBoardId}
            columns={columns || []}
            filterByDueDate={filterByDate}
          />
        </div>

        {numericCardId && (
          <Suspense fallback={<LoadingSpinner />}>
            <CardModal
              cardId={numericCardId}
              boardId={numericBoardId}
              onClose={handleCloseCard}
            />
          </Suspense>
        )}

        {showSettings && (
          <BoardSettings
            board={board}
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
          />
        )}
      </main>
    </MainLayout>
  )
}
